import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  /** A button inside the toast (e.g. "Reload"). Toasts with an action stay up longer. */
  action?: ToastAction
  duration?: number
}

interface ToastState {
  id: number
  message: string
  action?: ToastAction
}

type ShowToast = (message: string, options?: ToastOptions) => void

const ToastContext = createContext<ShowToast | null>(null)
const DURATION = 2800
const ACTION_DURATION = 8000

/** Single bottom-centre toast, auto-dismissed after 2.8s, announced politely. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null)
  const timer = useRef<number | undefined>(undefined)

  const show = useCallback<ShowToast>((message, options) => {
    window.clearTimeout(timer.current)
    setToast({ id: Date.now(), message, action: options?.action })
    timer.current = window.setTimeout(
      () => setToast(null),
      options?.duration ?? (options?.action ? ACTION_DURATION : DURATION),
    )
  }, [])

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-[max(20px,env(safe-area-inset-bottom))] z-70 flex justify-center px-4"
      >
        {toast && (
          <div
            key={toast.id}
            className="pointer-events-auto flex max-w-full animate-fade items-center gap-3 rounded-card bg-text px-4 py-2.5 text-[13.5px] font-medium text-surface shadow-pop"
          >
            <span>{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className="shrink-0 rounded-seg px-1.5 py-0.5 font-semibold text-surface underline underline-offset-2 hover:no-underline"
                onClick={() => {
                  window.clearTimeout(timer.current)
                  setToast(null)
                  toast.action?.onClick()
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ShowToast {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
